import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useI18n } from '../../i18n/I18nContext';
import { popoverRoot, useAnchoredPopover } from '../../hooks/useAnchoredPopover';
import { formatDate, formatMonthYear, formatPickerDate, todayYmd, weekdayShortLabels } from '../../utils/datetime';

/**
 * DatePicker — the CRM's own date field (spec §3.10; a port of the platform's
 * `date-picker.tsx`). Native <input type="date"> is banned: it draws the OS control and
 * follows the browser's locale.
 *
 *   <DatePicker value="2026-10-17" onChange={(ymd) => setDate(ymd)} min="2026-01-01" />
 *
 * Values are "YYYY-MM-DD" (or "" for none). The field reads "05 Oktabr 2026"; the panel
 * is Monday-first with Uzbek month names, opens under the field (above it when there is
 * more room), and is fully keyboard-driven: arrows move the day, PageUp / PageDown the
 * month (Shift: the year), Home / End the week, Enter picks, Esc closes (and does not also
 * close a dialog it sits in), Tab stays inside the panel.
 *
 * Props: value, onChange, min, max, placeholder, id, aria-label, className, disabled, invalid,
 * size ('md' 44px | 'sm' 40px toolbar field).
 */

const pad2 = (n) => String(n).padStart(2, '0');
const toIso = (y, m, d) => `${String(y).padStart(4, '0')}-${pad2(m + 1)}-${pad2(d)}`;
const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** "YYYY-MM-DD" -> { y, m (0-11), d }, or null when it is not a real calendar day. */
function parseYmd(value) {
  const match = DAY.exec(String(value || ''));
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]) - 1;
  const d = Number(match[3]);
  const probe = new Date(y, m, d, 12);
  return probe.getFullYear() === y && probe.getMonth() === m && probe.getDate() === d ? { y, m, d } : null;
}

const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
// Monday = 0 … Sunday = 6
const mondayIndex = (y, m, d = 1) => (new Date(y, m, d).getDay() + 6) % 7;

/** The cell a month grid starts with: how many blank cells before the 1st (Monday-first). */
export function leadingBlanks(year, monthIndex) {
  return mondayIndex(year, monthIndex, 1);
}

function DatePicker({
  value = '',
  onChange,
  min,
  max,
  placeholder,
  id,
  'aria-label': ariaLabel,
  className = '',
  disabled = false,
  invalid = false,
  size = 'md',
}) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => {
    const base = parseYmd(value) || parseYmd(todayYmd()) || { y: new Date().getFullYear(), m: new Date().getMonth() };
    return { y: base.y, m: base.m };
  });
  const [cursor, setCursor] = useState('');
  const anchorRef = useRef(null);
  const triggerRef = useRef(null);
  const popRef = useRef(null);
  const focusCursorRef = useRef(false);
  const pos = useAnchoredPopover(open, setOpen, anchorRef, popRef);

  const placeholderText = placeholder ?? t('ui.datePickerPlaceholder');
  const selected = parseYmd(value);
  const display = selected ? formatPickerDate(value, { lang }) : '';
  const weekdays = useMemo(() => weekdayShortLabels(lang), [lang]);
  const today = todayYmd();
  const outOfRange = useCallback(
    // ISO day strings compare in calendar order.
    (iso) => Boolean((min && iso < min) || (max && iso > max)),
    [min, max],
  );

  const openPanel = () => {
    const base = selected || parseYmd(today) || { y: view.y, m: view.m, d: 1 };
    setView({ y: base.y, m: base.m });
    setCursor(toIso(base.y, base.m, base.d || 1));
    focusCursorRef.current = true;
    setOpen(true);
  };

  const focusTrigger = () => {
    if (triggerRef.current) triggerRef.current.focus({ preventScroll: true });
  };

  const pick = (iso) => {
    if (outOfRange(iso)) return;
    onChange?.(iso);
    setOpen(false);
    focusTrigger();
  };

  const clear = () => {
    onChange?.('');
    setOpen(false);
    focusTrigger();
  };

  const goToday = () => {
    if (outOfRange(today)) return;
    pick(today);
  };

  const shiftMonth = (delta) => {
    const target = new Date(view.y, view.m + delta, 1);
    const y = target.getFullYear();
    const m = target.getMonth();
    setView({ y, m });
    const current = parseYmd(cursor);
    setCursor(toIso(y, m, Math.min(current ? current.d : 1, daysInMonth(y, m))));
  };

  const moveCursor = (y, m, d) => {
    focusCursorRef.current = true;
    setView({ y, m });
    setCursor(toIso(y, m, d));
  };

  // Put keyboard focus on the day the cursor rests on: when the panel first appears and
  // whenever an arrow key moved the cursor.
  const positioned = Boolean(pos);
  useLayoutEffect(() => {
    if (!open || !positioned || !focusCursorRef.current || !popRef.current) return;
    focusCursorRef.current = false;
    const cell = popRef.current.querySelector(`[data-iso="${cursor}"]`);
    if (cell) cell.focus({ preventScroll: true });
  }, [open, positioned, cursor, view]);

  const onPanelKeyDown = (e) => {
    if (e.key === 'Tab') {
      const items = Array.from(popRef.current?.querySelectorAll('button') || []).filter((b) => !b.disabled && b.tabIndex >= 0);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
      return;
    }
    const iso = e.target && e.target.dataset ? e.target.dataset.iso : null;
    const p = iso ? parseYmd(iso) : null;
    if (!p) return;
    let next = null;
    switch (e.key) {
      case 'ArrowLeft': next = new Date(p.y, p.m, p.d - 1); break;
      case 'ArrowRight': next = new Date(p.y, p.m, p.d + 1); break;
      case 'ArrowUp': next = new Date(p.y, p.m, p.d - 7); break;
      case 'ArrowDown': next = new Date(p.y, p.m, p.d + 7); break;
      case 'Home': next = new Date(p.y, p.m, p.d - mondayIndex(p.y, p.m, p.d)); break;
      case 'End': next = new Date(p.y, p.m, p.d + (6 - mondayIndex(p.y, p.m, p.d))); break;
      case 'PageUp':
      case 'PageDown': {
        const sign = e.key === 'PageUp' ? -1 : 1;
        const months = e.shiftKey ? 12 * sign : sign;
        const first = new Date(p.y, p.m + months, 1);
        next = new Date(first.getFullYear(), first.getMonth(), Math.min(p.d, daysInMonth(first.getFullYear(), first.getMonth())));
        break;
      }
      default: return;
    }
    e.preventDefault();
    moveCursor(next.getFullYear(), next.getMonth(), next.getDate());
  };

  const blanks = leadingBlanks(view.y, view.m);
  const total = daysInMonth(view.y, view.m);
  const title = formatMonthYear(view.y, view.m, lang);

  const panel = open ? (
    <div
      ref={popRef}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel || placeholderText}
      className="ui-picker-panel"
      style={pos ? { top: pos.top, left: pos.left, zIndex: pos.zIndex } : { top: 0, left: 0, opacity: 0, pointerEvents: 'none' }}
      onKeyDown={onPanelKeyDown}
    >
      <div className="ui-cal__head">
        <button type="button" className="ui-cal__nav" onClick={() => shiftMonth(-1)} aria-label={t('ui.datePickerPrevMonth')}>
          <ChevronLeft aria-hidden="true" />
        </button>
        <span className="ui-cal__title" aria-live="polite">{title}</span>
        <button type="button" className="ui-cal__nav" onClick={() => shiftMonth(1)} aria-label={t('ui.datePickerNextMonth')}>
          <ChevronRight aria-hidden="true" />
        </button>
      </div>

      <div className="ui-cal__weekdays" aria-hidden="true">
        {weekdays.map((name, i) => <span key={i}>{name}</span>)}
      </div>

      <div className="ui-cal__grid" role="group" aria-label={title}>
        {Array.from({ length: blanks }, (_, i) => <span key={`blank-${i}`} />)}
        {Array.from({ length: total }, (_, i) => {
          const day = i + 1;
          const iso = toIso(view.y, view.m, day);
          const isSelected = value === iso;
          const isToday = today === iso;
          const off = outOfRange(iso);
          const cls = `ui-cal__day${isSelected ? ' is-selected' : ''}${isToday ? ' is-today' : ''}`;
          return (
            <button
              key={iso}
              type="button"
              className={cls}
              data-iso={iso}
              tabIndex={iso === cursor ? 0 : -1}
              aria-pressed={isSelected}
              aria-current={isToday ? 'date' : undefined}
              aria-disabled={off || undefined}
              aria-label={formatDate(iso, { lang })}
              onClick={() => pick(iso)}
            >
              {day}
            </button>
          );
        })}
      </div>

      <div className="ui-picker__foot">
        <button type="button" className="ui-picker__link" onClick={clear}>
          {t('ui.datePickerClear')}
        </button>
        <button
          type="button"
          className="ui-picker__link ui-picker__link--accent"
          onClick={goToday}
          disabled={outOfRange(today)}
        >
          {t('ui.datePickerToday')}
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div ref={anchorRef} className={`ui-picker${className ? ` ${className}` : ''}`}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className={`ui-select${size === 'sm' ? ' ui-select--sm' : ''}${display ? '' : ' is-placeholder'}`}
        onClick={() => (open ? setOpen(false) : openPanel())}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel ? `${ariaLabel}${display ? `: ${display}` : ''}` : undefined}
        data-invalid={invalid || undefined}
        disabled={disabled}
      >
        <span className="ui-select__label">{display || placeholderText}</span>
        <Calendar className="ui-select__icon" aria-hidden="true" />
      </button>
      {panel && createPortal(panel, popoverRoot(anchorRef.current))}
    </div>
  );
}

export default DatePicker;
